import cx from 'classnames'
import { motion, AnimatePresence } from 'motion/react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { SanityHeroCaseVideoCarousel } from '@data/sanity/queries/types/modules'
import { fadeWithSlideAnimation } from '@lib/animate'
import { PageHeroContext } from '@lib/page-hero-context'
import HeroCaseVideoCarouselItem from './hero-case-video-carousel-item'
import HeroCaseVideoCarouselDot from './hero-case-video-carousel-dot'

type HeroCaseVideoCarouselProps = Pick<SanityHeroCaseVideoCarousel, 'items'>

const autoplayDelay = 7000
const animationDuration = 1000

const HeroCaseVideoCarousel = ({ items }: HeroCaseVideoCarouselProps) => {
  const { heroCaseVideoCarouselDisabled } = useContext(PageHeroContext)

  const ref = useRef<HTMLElement>(null)
  const slideTransitionTimeoutIdRef = useRef<NodeJS.Timeout>()

  const [activeIndex, setActiveIndex] = useState(0)
  const [isSlideTransition, setIsSlideTransition] = useState(false)

  const changeSlide = useCallback((index: number) => {
    setActiveIndex(index)

    // Disable previous transition's ending from altering state
    if (slideTransitionTimeoutIdRef.current) {
      clearTimeout(slideTransitionTimeoutIdRef.current)
    }

    // Initialize slide transition
    setIsSlideTransition(true)
    slideTransitionTimeoutIdRef.current = setTimeout(() => {
      setIsSlideTransition(false)
      slideTransitionTimeoutIdRef.current = undefined
    }, animationDuration)
  }, [])

  // Automatic slide change after a timeout
  useEffect(() => {
    if (heroCaseVideoCarouselDisabled) {
      return
    }

    const autoplayTimeoutId = setTimeout(() => {
      const nextIndex = (activeIndex + 1) % items.length
      changeSlide(nextIndex)
    }, autoplayDelay)

    return () => {
      clearTimeout(autoplayTimeoutId)
    }
  }, [activeIndex, heroCaseVideoCarouselDisabled, changeSlide, items.length])

  // Transition delay for content
  useEffect(() => {
    if (heroCaseVideoCarouselDisabled) {
      return
    }

    const slideElements = ref.current?.querySelectorAll('.slide')
    const activeSlideElement = slideElements?.[activeIndex]
    const activeSlideContentElements = activeSlideElement?.querySelectorAll(
      '.rc > *',
    ) as NodeListOf<HTMLElement> | undefined

    // Update transition delay of active slide content
    activeSlideContentElements?.forEach((element, index) => {
      element.style.transitionDelay = `${index * (animationDuration / 10)}ms`
    })
  }, [activeIndex, heroCaseVideoCarouselDisabled])

  return (
    <section
      ref={ref}
      className={cx(
        'relative overflow-hidden bg-pageText text-white',
        'h-screen max-h-[100svh]',
        {
          // Should hero size remain unchanged when opening a case? Previously when opening the case page,
          // it changed from 100vh to a specific aspect ratio, like so:
          // 'h-screen max-h-[100svh]': !heroCaseVideoCarouselDisabled,
          // 'max-h-screen': heroCaseVideoCarouselDisabled,
        },
      )}
    >
      {items.map((item, index) => (
        <HeroCaseVideoCarouselItem
          key={`hero-case-video-carousel-item-${index}`}
          muxVideo={item.casePage?.heroModule?.muxVideo}
          slideHeroContent={item.content}
          isSlideTransition={isSlideTransition}
          index={index}
          activeIndex={activeIndex}
        />
      ))}

      <AnimatePresence initial={false} mode="wait">
        {!heroCaseVideoCarouselDisabled && (
          <motion.ul
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeWithSlideAnimation}
            className="px-4 sm:container absolute bottom-4 sm:bottom-10 lg:bottom-14 inset-x-0 text-white flex flex-nowrap gap-x-3 z-20"
          >
            {items.map((_, index) => (
              <HeroCaseVideoCarouselDot
                key={`hero-case-video-carousel-dot-${index}`}
                changeSlide={changeSlide}
                index={index}
                activeIndex={activeIndex}
              />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </section>
  )
}

export default HeroCaseVideoCarousel
